import { flossByCode } from '../data/dmc';
import type { KnotType } from './knots';
import type {
  AlphaPattern,
  ChainModule,
  ChainPattern,
  ColorIndex,
  Design,
  MotifKind,
  NormalPattern,
  PaletteEntry,
  Sizing,
} from './types';

/**
 * Design <-> URL string.
 *
 * A design has to be shareable by link alone, with no backend and no accounts,
 * so the link has to carry the whole design. Two things make that practical:
 *
 *   1. Knot grids are extremely repetitive (a chevron is one row, repeated), so
 *      run-length encoding shrinks them enormously.
 *   2. Palette entries are stored as DMC code + hex only. Names come back from
 *      the local data file, and the stored hex means an old link still renders
 *      correctly even if the palette file later changes.
 *
 * The format is versioned. An unknown version fails loudly rather than
 * silently producing a bracelet that is not the one that was shared.
 */

export const DESIGN_FORMAT_VERSION = 1;

const KNOT_CODES: Record<KnotType, string> = { F: '0', B: '1', FB: '2', BF: '3' };
const CODE_KNOTS: Record<string, KnotType> = { '0': 'F', '1': 'B', '2': 'FB', '3': 'BF' };
const MOTIF_CODES: Record<MotifKind, string> = {
  daisy: 'd',
  cherry: 'c',
  strawberry: 's',
  heart: 'h',
};
const CODE_MOTIFS: Record<string, MotifKind> = { d: 'daisy', c: 'cherry', s: 'strawberry', h: 'heart' };

export class DesignDecodeError extends Error {}

/* -------------------------------------------------------------------------- */
/* Run-length encoding for the repetitive grids                               */
/* -------------------------------------------------------------------------- */

/** "000011" -> "4x0 2x1" packed as "40.21". Single items stay bare. */
export function rle(s: string): string {
  if (s.length === 0) return '';
  const parts: string[] = [];
  let i = 0;
  while (i < s.length) {
    const ch = s[i]!;
    let n = 1;
    while (i + n < s.length && s[i + n] === ch) n++;
    parts.push(n === 1 ? ch : `${n}${ch}`);
    i += n;
  }
  return parts.join('.');
}

export function unrle(s: string): string {
  if (s === '') return '';
  let out = '';
  for (const part of s.split('.')) {
    if (part.length === 0) continue;
    const m = part.match(/^(\d*)(.)$/);
    if (!m) throw new DesignDecodeError(`Corrupt run-length segment: "${part}"`);
    const count = m[1] === '' ? 1 : Number.parseInt(m[1]!, 10);
    if (!Number.isFinite(count) || count < 1 || count > 1_000_000) {
      throw new DesignDecodeError(`Implausible run length in "${part}"`);
    }
    out += m[2]!.repeat(count);
  }
  return out;
}

/* -------------------------------------------------------------------------- */
/* Base64url                                                                  */
/* -------------------------------------------------------------------------- */

export function toBase64Url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromBase64Url(s: string): string {
  const padded = s.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

/* -------------------------------------------------------------------------- */
/* Compact form                                                               */
/* -------------------------------------------------------------------------- */

interface CompactDesign {
  v: number;
  n: string;
  b: string;
  /** [dmcCode, hex] per palette entry. */
  p: [string, string][];
  /** [wristCm, easeCm, closureCode]. */
  z: [number, number, number];
  s: string;
  t: 'n' | 'a' | 'c';
  /** Normal: strand colours as digits. Chain: same. */
  c?: string;
  /** Normal: RLE of all knots, row-major. */
  r?: string;
  /** Alpha: RLE of the grid, row-major. */
  g?: string;
  /** Alpha: number of columns. */
  w?: number;
  /** Alpha: base colours as digits. */
  bc?: string;
  /** Alpha: RLE of leader colours. */
  lc?: string;
  /** Alpha: 1 when the first row runs right-to-left. */
  d?: number;
  /** Chain: modules, e.g. "c8.md1.c8". */
  m?: string;
}

/** Colour indices are stored as single base-36 digits, so max palette is 36. */
function colorsToDigits(colors: ColorIndex[]): string {
  return colors
    .map((c) => {
      if (c < 0 || c > 35) throw new Error(`Colour index ${c} out of encodable range (0-35).`);
      return c.toString(36);
    })
    .join('');
}

function digitsToColors(s: string): ColorIndex[] {
  return [...s].map((ch) => {
    const v = Number.parseInt(ch, 36);
    if (Number.isNaN(v)) throw new DesignDecodeError(`Bad colour digit "${ch}"`);
    return v;
  });
}

export function encodeDesign(design: Design): string {
  const compact = toCompact(design);
  return toBase64Url(JSON.stringify(compact));
}

export function decodeDesign(encoded: string): Design {
  let parsed: unknown;
  try {
    parsed = JSON.parse(fromBase64Url(encoded));
  } catch (err) {
    throw new DesignDecodeError(
      `That link is not a readable design. It may have been truncated when it was copied. (${(err as Error).message})`,
    );
  }
  return fromCompact(parsed as CompactDesign);
}

function toCompact(design: Design): CompactDesign {
  const base: CompactDesign = {
    v: DESIGN_FORMAT_VERSION,
    n: design.name,
    b: design.blurb,
    p: design.palette.map((e) => [e.dmc, e.hex] as [string, string]),
    z: [design.sizing.wristCm, design.sizing.easeCm, design.sizing.closure === 'tie' ? 0 : 1],
    s: design.seed,
    t: design.body.style === 'normal' ? 'n' : design.body.style === 'alpha' ? 'a' : 'c',
  };

  if (design.body.style === 'normal') {
    const body = design.body;
    base.c = colorsToDigits(body.strandColors);
    base.r = rle(body.rows.flat().map((k) => KNOT_CODES[k]).join(''));
  } else if (design.body.style === 'alpha') {
    const body = design.body;
    base.w = body.baseColors.length;
    base.g = rle(body.grid.map((row) => colorsToDigits(row)).join(''));
    base.bc = colorsToDigits(body.baseColors);
    base.lc = rle(colorsToDigits(body.leaderColors));
    base.d = body.firstRowDirection === 'rtl' ? 1 : 0;
  } else {
    const body = design.body;
    base.c = colorsToDigits(body.strandColors);
    base.m = body.modules
      .map((m) => (m.kind === 'chain' ? `c${m.knots}` : `m${MOTIF_CODES[m.motif]}${m.rounds}`))
      .join('.');
  }
  return base;
}

function fromCompact(c: CompactDesign): Design {
  if (!c || typeof c !== 'object') throw new DesignDecodeError('That link does not contain a design.');
  if (c.v !== DESIGN_FORMAT_VERSION) {
    throw new DesignDecodeError(
      `This link was made with design format v${c.v}, but this version of Knotwright reads v${DESIGN_FORMAT_VERSION}. ` +
        `Rather than guess and hand you the wrong bracelet, it is refusing to open it.`,
    );
  }
  if (!Array.isArray(c.p) || c.p.length === 0) {
    throw new DesignDecodeError('That link has no colours in it.');
  }

  const palette: PaletteEntry[] = c.p.map(([dmc, hex]) => {
    const known = flossByCode(dmc);
    return {
      dmc,
      hex: hex || known?.hex || '#888888',
      name: known?.name ?? `DMC ${dmc}`,
    };
  });

  const sizing: Sizing = {
    wristCm: numberOr(c.z?.[0], 15.5),
    easeCm: numberOr(c.z?.[1], 1.5),
    closure: c.z?.[2] === 1 ? 'loop-and-tie' : 'tie',
  };

  let body: Design['body'];
  if (c.t === 'n') {
    body = decodeNormal(c, palette.length);
  } else if (c.t === 'a') {
    body = decodeAlpha(c, palette.length);
  } else if (c.t === 'c') {
    body = decodeChain(c, palette.length);
  } else {
    throw new DesignDecodeError(`Unknown bracelet style code "${c.t}".`);
  }

  return {
    v: 1,
    name: typeof c.n === 'string' ? c.n : 'Shared design',
    blurb: typeof c.b === 'string' ? c.b : '',
    palette,
    sizing,
    seed: typeof c.s === 'string' ? c.s : '',
    body,
  };
}

function decodeNormal(c: CompactDesign, paletteSize: number): NormalPattern {
  if (!c.c || !c.r) throw new DesignDecodeError('Incomplete normal pattern in link.');
  const strandColors = digitsToColors(c.c);
  checkColors(strandColors, paletteSize);
  const n = strandColors.length;
  if (n < 2 || n % 2 !== 0) {
    throw new DesignDecodeError(`Link describes ${n} strings; a normal pattern needs an even count.`);
  }

  const flat = unrle(c.r);
  const rows: KnotType[][] = [];
  let i = 0;
  let r = 0;
  while (i < flat.length) {
    const want = r % 2 === 0 ? n / 2 : n / 2 - 1;
    if (i + want > flat.length) {
      throw new DesignDecodeError('Link ends part-way through a row of knots.');
    }
    const row: KnotType[] = [];
    for (let k = 0; k < want; k++) {
      const knot = CODE_KNOTS[flat[i + k]!];
      if (!knot) throw new DesignDecodeError(`Unknown knot code "${flat[i + k]}" in link.`);
      row.push(knot);
    }
    rows.push(row);
    i += want;
    r++;
  }
  return { style: 'normal', strandColors, rows };
}

function decodeAlpha(c: CompactDesign, paletteSize: number): AlphaPattern {
  if (!c.g || !c.bc || !c.lc || !c.w) throw new DesignDecodeError('Incomplete alpha pattern in link.');
  const cols = c.w;
  const flat = digitsToColors(unrle(c.g));
  if (cols <= 0 || flat.length % cols !== 0) {
    throw new DesignDecodeError('Alpha grid in link is not a whole number of rows.');
  }
  const grid: ColorIndex[][] = [];
  for (let i = 0; i < flat.length; i += cols) grid.push(flat.slice(i, i + cols));

  const baseColors = digitsToColors(c.bc);
  const leaderColors = digitsToColors(unrle(c.lc));
  checkColors([...flat, ...baseColors, ...leaderColors], paletteSize);

  if (baseColors.length !== cols) {
    throw new DesignDecodeError('Link has a different number of base strings than grid columns.');
  }
  if (leaderColors.length !== grid.length) {
    throw new DesignDecodeError('Link has a different number of leader colours than rows.');
  }

  return {
    style: 'alpha',
    grid,
    baseColors,
    leaderColors,
    firstRowDirection: c.d === 1 ? 'rtl' : 'ltr',
  };
}

function decodeChain(c: CompactDesign, paletteSize: number): ChainPattern {
  if (!c.c || c.m === undefined) throw new DesignDecodeError('Incomplete chain pattern in link.');
  const strandColors = digitsToColors(c.c);
  checkColors(strandColors, paletteSize);
  if (strandColors.length !== 6) {
    throw new DesignDecodeError(`Chain bracelets use 6 strands; link has ${strandColors.length}.`);
  }

  const modules: ChainModule[] = [];
  for (const part of c.m.split('.').filter(Boolean)) {
    if (part.startsWith('c')) {
      const knots = Number.parseInt(part.slice(1), 10);
      if (!Number.isFinite(knots) || knots < 0 || knots > 500) {
        throw new DesignDecodeError(`Bad chain length "${part}" in link.`);
      }
      modules.push({ kind: 'chain', knots });
    } else if (part.startsWith('m')) {
      const motif = CODE_MOTIFS[part[1]!];
      const rounds = Number.parseInt(part.slice(2), 10);
      if (!motif || !Number.isFinite(rounds) || rounds < 1 || rounds > 20) {
        throw new DesignDecodeError(`Bad motif "${part}" in link.`);
      }
      modules.push({ kind: 'motif', motif, rounds });
    } else {
      throw new DesignDecodeError(`Unknown module "${part}" in link.`);
    }
  }
  return { style: 'chain', strandColors, modules };
}

function checkColors(colors: ColorIndex[], paletteSize: number): void {
  for (const c of colors) {
    if (c < 0 || c >= paletteSize) {
      throw new DesignDecodeError(
        `Link refers to colour ${c}, but it only carries ${paletteSize} colours.`,
      );
    }
  }
}

function numberOr(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}
